import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Calendar } from 'lucide-react';
import api from '../../services/api';
import '../../styles/AgendaSettimanaConsole.css';
import { mescola } from '../../utils/colori';
import { oggiClinica, giornoIta, oraDecimaleIta, oraIta } from '../../utils/oraItaliana';
import BloccoAppuntamento from './BloccoAppuntamento';
import { usePagineDiColonne, NavigazionePagine } from './PagineDiColonne';
import {
    ORE, ORA_INIZIO, ORA_FINE, ALTEZZA_ORA, dueCifre, pixelDi, pixelDiOrario, disponi, riquadroDi, oraCoperta, useAdesso,
} from './griglia';

/**
 * AgendaSettimana — una sola agenda su sette giorni.
 *
 * Non tutte le agende su tutta la settimana: con dodici medici in sede
 * sarebbe illeggibile. Il medico apre la propria settimana, la
 * segreteria sceglie di chi guardarla dalla tendina in testa all'Agenda.
 *
 * DAL 6/10/2026 (stile «Console») la settimana non ha piu' una testata
 * sua: «‹ Oggi ›» e la scelta dell'agenda stanno nella testata dell'Agenda,
 * le stesse della vista del giorno, e la settimana e' quella del giorno
 * nell'indirizzo (?giorno=). Gli appuntamenti si disegnano come nel giorno
 * (BloccoAppuntamento), col colore dell'agenda, i pallini, «Sovrapposto»
 * e la linea dell'ora; un clic apre il pannello del dettaglio a destra.
 * Le ore sono quelle della CLINICA (griglia.js): prima qui si usava l'ora
 * del browser, e il giorno di un appuntamento era quello UTC.
 */

const GIORNI = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

/* I conti sui giorni si fanno a mezzogiorno UTC e con i metodi UTC:
   cosi' non dipendono dal fuso del browser e non inciampano nei giorni
   del cambio d'ora, che sono lunghi 23 o 25 ore. */
const aMezzogiornoUtc = (iso) => new Date(`${iso}T12:00:00Z`);

/** Il lunedi' della settimana in cui cade la data indicata. */
export const lunediDi = (iso) => {
    const d = aMezzogiornoUtc(iso);
    const scarto = (d.getUTCDay() + 6) % 7;   // domenica in JS e' 0
    d.setUTCDate(d.getUTCDate() - scarto);
    return d.toISOString().slice(0, 10);
};

export const piuGiorni = (iso, n) => {
    const d = aMezzogiornoUtc(iso);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
};

const AgendaSettimana = ({ agende, agendaId, dal, onApri, scelto = null, versione = 0 }) => {
    /* Il colore e' quello dell'agenda scelta: la stanza quasi sempre non ne
       ha, e lo sfondo restava grigio. */
    const coloreAgenda = (agende || []).find((a) => Number(a.id) === Number(agendaId))?.colore || null;
    const [dati, setDati] = useState(null);
    const [caricamento, setCaricamento] = useState(false);
    const [errore, setErrore] = useState(null);
    const adesso = useAdesso();

    const carica = useCallback(async () => {
        if (!agendaId) { setDati(null); return; }
        try {
            setCaricamento(true);
            setErrore(null);
            const r = await api.get('/agenda/settimana', { params: { dal, agenda_id: agendaId } });
            setDati(r.data.data);
        } catch (e) {
            setErrore(e?.response?.data?.message || 'Impossibile caricare la settimana');
        } finally {
            setCaricamento(false);
        }
    }, [dal, agendaId]);

    /* Come il giorno: all'apertura di una settimana che contiene oggi la
       griglia va sull'ora di adesso, una volta sola (le riletture dopo un
       gesto non la spostano). */
    /* I giorni che non entrano a 104 px (a 1024 sono sei, a 390 uno o due)
       stanno nelle pagine dietro le frecce, come le colonne del giorno: la
       settimana non scorre mai di lato (Alex, 22/10/2026). */
    const pagine = usePagineDiColonne(GIORNI.length);
    const corpo = useRef(null);
    const misuraCorpo = pagine.osserva;
    const agganciaCorpo = useCallback((el) => {
        corpo.current = el;
        misuraCorpo(el);
    }, [misuraCorpo]);
    const posizionata = useRef(null);
    useEffect(() => {
        const chiave = `${agendaId}|${dal}`;
        if (!dati || !corpo.current || posizionata.current === chiave) return;
        posizionata.current = chiave;
        const ora = Math.floor(oraDecimaleIta(new Date()));
        const settimana = GIORNI.map((_, i) => piuGiorni(dal, i));
        if (settimana.includes(oggiClinica()) && ora > ORA_INIZIO + 2) {
            corpo.current.scrollTop = (ora - ORA_INIZIO - 1) * ALTEZZA_ORA;
        }
    }, [dati, agendaId, dal]);

    /* `versione` sale quando l'Agenda sa che qualcosa e' cambiato (un
       arrivo, uno spostamento dal pannello, un'altra scheda che annuncia
       l'agenda cambiata): la settimana si rilegge con lei. */
    useEffect(() => { carica(); }, [carica, versione]);

    const giorni = useMemo(() => GIORNI.map((nome, i) => ({ nome, n: i + 1, data: piuGiorni(dal, i) })), [dal]);
    const giorniInVista = giorni.slice(pagine.inizio, pagine.fine);
    const oggi = oggiClinica(adesso);

    const fascePer = (n) => (dati?.fasce || []).filter((f) => f.giorno === n);
    /* Il giorno di un appuntamento e' quello ITALIANO del suo inizio: prima
       si tagliava la stringa dell'istante, cioe' il giorno UTC, e fra
       mezzanotte e le due un appuntamento finiva nella colonna di ieri. */
    const appPer = (g) => disponi((dati?.appuntamenti || []).filter((a) => giornoIta(a.inizio) === g));

    /* La linea dell'ora, solo sulla colonna di oggi. */
    const oraAdesso = oraDecimaleIta(adesso);
    const lineaAdesso = oraAdesso >= ORA_INIZIO && oraAdesso <= ORA_FINE + 1
        ? (oraAdesso - ORA_INIZIO) * ALTEZZA_ORA : null;
    const oggiInVista = giorniInVista.some((g) => g.data === oggi);

    if (!agendaId) {
        return (
            <div className="sett-stato">
                <Calendar size={28} aria-hidden="true" />
                <p>Scegli un&apos;agenda per vederne la settimana.</p>
            </div>
        );
    }

    return (
        <div className="sett-wrap">
            {errore && <div className="ag-errore">{errore}</div>}
            {caricamento && !dati ? (
                <div className="sett-stato">Caricamento…</div>
            ) : (
                <div className="sett-griglia" style={{ '--ag-ora': `${ALTEZZA_ORA}px`, '--ag-giorni': giorniInVista.length }}>
                    {pagine.conPagine && (
                        <div className="sett-pagine">
                            <NavigazionePagine cosa="giorni" pagine={pagine} totale={GIORNI.length} />
                        </div>
                    )}
                    <div className="sett-corpo" ref={agganciaCorpo}>
                        <div className="sett-intestazioni">
                            <div className="sett-angolo" />
                            {giorniInVista.map((g) => (
                                <div key={g.n} className={`sett-testa ${g.data === oggi ? 'oggi' : ''}`}>
                                    <b>{g.nome}</b>
                                    <span className="rm-numeri">{Number(g.data.slice(8, 10))}</span>
                                </div>
                            ))}
                        </div>
                        <div className="sett-interno" style={{ height: (ORA_FINE - ORA_INIZIO + 1) * ALTEZZA_ORA }}>
                            <div className="sett-ore">
                                {ORE.map((h) => (
                                    <div key={h} className={`sett-ora${oggiInVista && oraCoperta(h, lineaAdesso) ? ' ag-ora--coperta' : ''}`}
                                         style={{ height: ALTEZZA_ORA }}>
                                        <span className="rm-numeri">{dueCifre(h)}:00</span>
                                    </div>
                                ))}
                                {lineaAdesso !== null && oggiInVista && (
                                    <span className="ag-adesso-ora rm-numeri" style={{ top: lineaAdesso }}>
                                        {oraIta(adesso)}
                                    </span>
                                )}
                            </div>

                            {giorniInVista.map((g) => (
                                <div key={g.n} className={`sett-colonna ${g.data === oggi ? 'oggi' : ''}`}
                                     data-giorno={g.data}>
                                    {fascePer(g.n).map((f, i) => (
                                        <div key={i} className="sett-fascia"
                                             style={{
                                                 top: pixelDiOrario(f.ora_inizio),
                                                 height: pixelDiOrario(f.ora_fine) - pixelDiOrario(f.ora_inizio),
                                                 ...(coloreAgenda ? { background: mescola(coloreAgenda, 0.93) } : {}),
                                             }} />
                                    ))}

                                    {appPer(g.data).map((x) => {
                                        const largh = 100 / x.insieme;
                                        const { top, height } = riquadroDi(x);
                                        return (
                                            <BloccoAppuntamento
                                                key={x.id}
                                                x={x}
                                                colore={coloreAgenda}
                                                scelto={Number(scelto) === Number(x.id)}
                                                stile={{
                                                    top,
                                                    height,
                                                    left: `${x.colonna * largh}%`,
                                                    width: `calc(${largh}% - var(--rm-s1))`,
                                                }}
                                                onClick={() => onApri && onApri(x)}
                                            />
                                        );
                                    })}

                                    {g.data === oggi && lineaAdesso !== null && (
                                        <div className="ag-adesso ag-adesso--colonna" style={{ top: lineaAdesso }} />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AgendaSettimana;
